import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Sparkles, Check, X, Gauge } from "lucide-react";
import { toast } from "sonner";

export default function LearningEngine({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "it" ? it : en);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = () => {
    if (!propId) return;
    api.get(`/properties/${propId}/learning-suggestion?lang=${lang}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, [propId, lang]);

  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/properties/${propId}/learning-aggressiveness`, { action });
      toast.success(L(`Aggressività aggiornata a ${r.data.pricing_aggressiveness}×`, `Aggressiveness set to ${r.data.pricing_aggressiveness}×`));
      load();
    } catch { toast.error(L("Errore", "Error")); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const aggr = data.current_aggressiveness ?? 1.0;
  const showSuggest = data.should_suggest && !dismissed;

  return (
    <motion.div data-testid="learning-engine" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">{L("Learning Engine", "Learning Engine")}</h3>
          <span className="flex items-center gap-1 text-xs text-muted-foreground ml-2"><Gauge className="w-3.5 h-3.5" /> {L("Aggressività modello", "Model aggressiveness")}: <b className="text-foreground">{aggr.toFixed(2)}×</b></span>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="le-decrease" onClick={() => act("decrease")} disabled={busy || aggr <= 0.85} className="w-7 h-7 rounded border border-border hover:bg-accent disabled:opacity-40">−</button>
          <button data-testid="le-reset" onClick={() => act("reset")} disabled={busy} className="px-2 h-7 text-xs rounded border border-border hover:bg-accent">1.00×</button>
          <button data-testid="le-increase" onClick={() => act("increase")} disabled={busy || aggr >= 1.20} className="w-7 h-7 rounded border border-border hover:bg-accent disabled:opacity-40">+</button>
        </div>
      </div>

      {showSuggest ? (
        <div className="mt-3 rounded-lg bg-primary/5 border border-primary/20 p-3" data-testid="le-suggestion">
          <p className="text-sm mb-3">{data.message}</p>
          <div className="flex items-center gap-2">
            <button data-testid="le-accept" onClick={() => { act(data.direction); }} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
              <Check className="w-4 h-4" /> {data.direction === "increase" ? L("Aumenta aggressività", "Increase aggressiveness") : L("Rendi più prudente", "Make more conservative")}
            </button>
            <button data-testid="le-dismiss" onClick={() => setDismissed(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border hover:bg-accent">
              <X className="w-4 h-4" /> {L("Non ora", "Not now")}
            </button>
          </div>
          {data.evidence && (
            <div className="text-[11px] text-muted-foreground mt-2">
              {L("Basato su", "Based on")} {data.evidence.comparable_days} {L("giorni confrontabili", "comparable days")} · {L("venduto sopra il consiglio", "sold above advice")} {data.evidence.sold_above} · {L("sotto", "below")} {data.evidence.sold_below}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mt-2" data-testid="le-status">{data.message}</p>
      )}
    </motion.div>
  );
}
